import { useCallback, useRef } from 'react';

import { filterNullishValuesFromObject } from '@pkg/utils';

import { useEventListener } from '../useEventListener';
import { useIsoLayoutEffect } from '../useIsoLayoutEffect';

import { type KeyPressCallback, KeyPressEventType, type KeyPressHookOptions, type KeyPressInput } from './types';

const DEFAULT_OPTIONS: Required<KeyPressHookOptions> = {
    eventType: KeyPressEventType.Down,
    target: document,
    disabled: false,
};

export function useKeyPress(keys: KeyPressInput, callback: KeyPressCallback, options?: KeyPressHookOptions) {
    const { eventType, target, disabled } = {
        ...DEFAULT_OPTIONS,
        ...filterNullishValuesFromObject<KeyPressHookOptions>(options ?? {}),
    };

    const callbackRef = useRef(callback);

    const handleCallback: KeyPressCallback = useCallback(
        (event) => {
            const requiredKeysEngaged = keys.some((key) => event.key === key);

            if (requiredKeysEngaged) {
                callbackRef.current?.(event);
            }
        },
        [keys],
    );

    useIsoLayoutEffect(() => {
        callbackRef.current = callback;
    }, [callback]);

    useEventListener(target, eventType, handleCallback, { disabled });
}
